import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { ErrorState } from '@/shared/feedback';

/**
 * The credential rotation dialog.
 *
 * ---------------------------------------------------------------------------
 * WRITE-ONLY, BY CONSTRUCTION
 * ---------------------------------------------------------------------------
 * The key lives in this component's state for as long as it takes to type it
 * and press the button. It is sent once, and it is cleared the moment the
 * submit is handed over — it is never written back into the input, never
 * echoed, never logged, and never stored anywhere the page could show it
 * again. The API does not return it, so there is nothing to re-populate.
 *
 * The trigger is a real button, so the dialog returns focus to it when it
 * closes — the operator's place in the page is not lost after a rotation.
 */
export type RotateCredentialDialogProps = {
  /** The provider the key belongs to, named in the dialog. */
  providerName: string;
  isSubmitting: boolean;
  error: Error | null;
  /** Called with the new key. The key is never passed back to this component. */
  onSubmit: (apiKey: string) => void;
};

export function RotateCredentialDialog({
  providerName,
  isSubmitting,
  error,
  onSubmit,
}: RotateCredentialDialogProps): ReactNode {
  const [apiKey, setApiKey] = useState('');

  const handleSubmit = (): void => {
    const trimmed = apiKey.trim();
    if (trimmed === '') return;
    onSubmit(trimmed);
    setApiKey('');
  };

  return (
    <Dialog>
      <DialogTrigger>Rotate credential</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate credential</DialogTitle>
          <DialogDescription>
            Enter a new API key for {providerName}. The key is sent once and is never shown again
            — only its status, version and last-rotation date are stored.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error ? <ErrorState title="Could not rotate the credential" message={error.message} /> : null}

          <Input
            label="New API key"
            type="password"
            value={apiKey}
            onChange={(event) => {
              setApiKey(event.currentTarget.value);
            }}
            hint="Write-only. It replaces the stored key for this provider."
            autoComplete="off"
            maxLength={500}
          />
        </div>

        <DialogFooter>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={apiKey.trim() === ''}
          >
            Rotate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
